import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { compressImage } from '../../../utils/Lots';
import Api from '../../../services/api';
import { limitCharacters } from '../../../utils';

const FileUploadField = ({ value = [], onChange, disabled = false, lotId, showSpinner, field }) => {
  const MAX_DOCUMENT_LIMIT = 6;
  const [documents, setDocuments] = useState(value);

  useEffect(() => {
    if (!documents.length) addEmptyRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getNextOrder = () => {
    let maxOrder = Math.max(...documents.map(doc => doc.order));
    return maxOrder >= 0 ? maxOrder + 1 : 0;
  };

  const addEmptyRow = () => {
    if (documents.length < MAX_DOCUMENT_LIMIT) {
      const newDoc = {
        file: null,
        name: '',
        mimeType: '',
        description: '',
        order: getNextOrder(),
        isNew: true,
      };
      const updated = update(documents, { $push: [newDoc] });
      setDocuments(updated);
      onChange && onChange(transformForBackend(updated));
    }
  };

  // Transform frontend documents to backend schema
  const transformForBackend = (docs) => {
    return docs
      .filter(d => d.key || d.uri) // only include uploaded or pending-upload files
      .map(d => ({
        key: d.key || '',
        name: d.name,
        mimetype: d.mimetype || d.mimeType || (d.file?.type || ''),
        description: d.description || '',
        order: d.order,
      }));
  };

  const handleFileChange = (event, document) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileReader = new FileReader();
    fileReader.onloadend = async () => {
      let newDocument = {
        file,
        name: file.name,
        uri: fileReader.result,
        mimeType: file.type || '',
        description: document.description || '',
        order: document.order,
      };

      // Set mimeType if missing
      if (!file.type) {
        const ext = file.name.split('.').pop();
        switch (ext) {
          case 'xls': newDocument.mimeType = 'application/vnd.ms-excel'; break;
          case 'xlsx': newDocument.mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; break;
          case 'doc': newDocument.mimeType = 'application/msword'; break;
          case 'docx': newDocument.mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; break;
          default: newDocument.mimeType = '';
        }
      }

      const saveDoc = async (doc) => {
        const idx = documents.indexOf(document);
        const updated = update(documents, {
          [idx]: { $merge: doc }
        });
        setDocuments(updated);

        // Upload file to server if lotId exists
        if (lotId && doc.uri) {
          showSpinner && showSpinner(true);
          try {
            const res = await Api.postLotDocuments(lotId, doc);
            if (res && res.document) {
              updated[idx] = {
                ...updated[idx],
                ...res.document,
                file: null,
                uri: null,
                isNew: false,
              };
              setDocuments([...updated]);
              onChange && onChange(transformForBackend(updated));
            }
          } catch (err) {
            console.error(err);
          } finally {
            showSpinner && showSpinner(false);
          }
        } else {
          onChange && onChange(transformForBackend(updated));
        }
      };

      if (/image/.test(file.type)) {
        compressImage(newDocument, { convertSize: 1000, quality: 0.7, mimeType: 'image/jpeg' })
          .then(saveDoc)
          .catch(console.error);
      } else {
        saveDoc(newDocument);
      }
    };
    fileReader.readAsDataURL(file);
    event.target.value = null;
  };

  const handleDescriptionChange = (event, document) => {
    const value = event.target.value;
    const updated = update(documents, { [documents.indexOf(document)]: { description: { $set: value } } });
    setDocuments(updated);
    onChange && onChange(transformForBackend(updated));
  };

  const removeDocument = async (document) => {
    if (lotId && document.key) {
      showSpinner && showSpinner(true);
      try {
        await Api.deleteLotDocument(lotId, document.key);
      } catch (err) {
        console.error(err);
      } finally {
        showSpinner && showSpinner(false);
      }
    }
    const updated = documents.filter(d => d !== document);
    setDocuments(updated);
    onChange && onChange(transformForBackend(updated));
  };

  return (
    <div className={`input-section input-section-documents ${disabled ? 'disabled' : ''}`}>
      <div className='section-heading'>
        <div className='heading-title'>{field?.title || 'Upload Files'}</div>
      </div>
      <div className='section-body'>
        {documents.map((document, index) => (
          <div className='form-row' key={index}>
            <div className='row-title'>
              <p className='row-text'>Select Document:</p>
            </div>
            <div className='row-content'>
              <label className='file-label documents-text-wrapper'>
                {document.name ? limitCharacters(document.name) : 'Add New File'}
                {!disabled && (
                  <input
                    type='file'
                    className='file-input'
                    onChange={e => handleFileChange(e, document)}
                    accept={field?.acceptType || '.jpg,.jpeg,.pdf,.png,.doc,.docx,.xls,.xlsx'}
                  />
                )}
              </label>
              <input
                className='input documents-description documents-text-wrapper'
                type='text'
                placeholder='Description'
                value={document.description || ''}
                onChange={e => handleDescriptionChange(e, document)}
                disabled={disabled}
              />
              {!disabled && (
                <>
                  <span className='doc-btn doc-btn-add' onClick={addEmptyRow}>+</span>
                  <span className='doc-btn doc-btn-remove' onClick={() => removeDocument(document)}>-</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

FileUploadField.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  lotId: PropTypes.string,
  showSpinner: PropTypes.func,
  field: PropTypes.object
};

export default FileUploadField;
